import './RecentPatients.css'
import React, { useMemo } from 'react'
import { Button, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { AlertFilled } from '@ant-design/icons'
import { UserData } from '../../../types/user/types'
import { isHighBloodPressure } from '../../../utils/isHighBloodPressure'
import { formatRankStatus } from '../../../utils/formatRankStatus'
import { getLastTestDate } from '../../../utils/getLastTestDate'

dayjs.extend(relativeTime)

interface DataType {
  userId: string
  name: string
  isNew: boolean
  isHidden: boolean
  sortTimestamp: number
  lastActive: string
  rank1: string
  rank2: string
  frailty: string
  highBloodPressure: boolean
}

interface RecentPatientsProps {
  users: UserData[]
  rank1ByUser: Record<string, string>
  rank2ByUser: Record<string, string>
  frailtyByUser: Record<string, string>
  loading?: boolean
  isAdmin?: boolean
}

const getStatusColor = (reading: string): string => {
  switch (reading.toLowerCase()) {
    case 'doing well':
      return 'cyan'
    case 'aim to improve':
    case 'pre-frail':
      return 'orange'
    case 'frail':
      return 'red'
    case 'error':
    case 'not finished':
    default:
      return 'default'
  }
}

const StatusTag: React.FC<{ value: string }> = ({ value }) => (
  <Tag color={getStatusColor(value)} style={{ marginInlineEnd: 0 }}>
    {formatRankStatus(value)}
  </Tag>
)

export default function RecentPatients({
  users,
  rank1ByUser,
  rank2ByUser,
  frailtyByUser,
  loading,
  isAdmin,
}: RecentPatientsProps) {
  const navigate = useNavigate()

  const paginationConfig: TablePaginationConfig = {
    position: ['bottomCenter'],
  }

  const patients: DataType[] = useMemo(
    () =>
      users
        .filter((user) => isAdmin || (user as any).otherInformation?.hiddenFromPortal !== true)
        .map((user) => ({
          userId: user.userId,
          name: user.username,
          isNew: !!user.creationTimestamp && dayjs().diff(dayjs(user.creationTimestamp), 'day') <= 14,
          isHidden: (user as any).otherInformation?.hiddenFromPortal === true,
          sortTimestamp: Math.max(
            getLastTestDate(user)?.valueOf() ?? 0,
            user.creationTimestamp ? dayjs(user.creationTimestamp).valueOf() : 0
          ),
          lastActive: getLastTestDate(user)?.toISOString() ?? '',
          rank1: rank1ByUser[user.userId] || 'No Record',
          rank2: rank2ByUser[user.userId] || 'No Record',
          frailty: frailtyByUser[user.userId] || 'No Record',
          highBloodPressure: isHighBloodPressure(user),
        }))
        .sort((a, b) => b.sortTimestamp - a.sortTimestamp)
        .filter((_, index) => index < 50), // TODO: Remove manual top 50 filter
    [users, rank1ByUser, rank2ByUser, frailtyByUser, isAdmin]
  )

  const viewPatient = (userId: string) => {
    navigate(`/patient/${userId}`)
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {name}
          {record.isNew && (
            <Tooltip title='Registered within the last 14 days'>
              <Tag color='green' style={{ marginInlineEnd: 0 }}>
                New
              </Tag>
            </Tooltip>
          )}
          {record.isHidden && <Tag style={{ marginInlineEnd: 0 }}>Hidden</Tag>}
          {record.highBloodPressure && (
            <Tooltip title='High blood pressure on latest reading'>
              <AlertFilled style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Rank 1',
      dataIndex: 'rank1',
      key: 'rank1',
      responsive: ['md'],
      sorter: (a, b) => a.rank1.localeCompare(b.rank1),
      render: (value: string) => <StatusTag value={value} />,
    },
    {
      title: 'Rank 2',
      dataIndex: 'rank2',
      key: 'rank2',
      responsive: ['md'],
      sorter: (a, b) => a.rank2.localeCompare(b.rank2),
      render: (value: string) => <StatusTag value={value} />,
    },
    {
      title: 'Frailty',
      dataIndex: 'frailty',
      key: 'frailty',
      responsive: ['md'],
      sorter: (a, b) => a.frailty.localeCompare(b.frailty),
      render: (value: string) => <StatusTag value={value} />,
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActive',
      key: 'lastActive',
      responsive: ['md'],
      sorter: (a, b) => a.sortTimestamp - b.sortTimestamp,
      defaultSortOrder: 'descend',
      render: (lastActive: string) => (lastActive ? dayjs(lastActive).fromNow() : '—'),
    },
    {
      title: '',
      key: 'viewDetails',
      render: (_, record: any) => (
        <Button
          type='text'
          onClick={() => viewPatient(record.userId)}
          style={{
            height: '32px',
            padding: '0px 16px',
            borderRadius: '5px',
            border: '1px solid #316BFF',
            color: '#316BFF',
          }}>
          View details
        </Button>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={patients}
      loading={loading}
      scroll={{ y: 480 }}
      pagination={paginationConfig}
      rowClassName={() => 'custom-row-class'}
      components={{
        body: {
          row: ({ children, ...restProps }: { children: React.ReactNode; [key: string]: any }) => (
            <tr className='custom-row-class' {...restProps}>
              {children}
            </tr>
          ),
        },
      }}
    />
  )
}
